import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useApi } from "../utils/Api";
import PersonalisationModal from "../components/PersonalisationModal";

const money = (n) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(Number(n || 0));

const INITIAL_VISIBLE_OPTIONS = 6;
const PHOTO_PRINT_CATEGORY_ID = 2;
const UPLOADS_BASE_URL = "https://kinewsstudiolimited.co.uk";

function buildUploadedFileUrl(path) {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${UPLOADS_BASE_URL}/${String(path).replace(/^\/+/, "")}`;
}

function getPrimaryImage(product) {
  const image = product.image_url;

  if (!image) {
    return "/placeholder-product.jpg";
  }

  if (image.startsWith("http://") || image.startsWith("https://")) {
    return image;
  }

  return `https://kinewsstudiolimited.co.uk/${image.replace(/^\/+/, "")}`;
}

function getDefaultSelections(product) {
  const selections = {};

  (product.option_groups || []).forEach((group) => {
    const values = (group.values || []).filter(
      (value) => Number(value.is_active) === 1,
    );
    if (!values.length) return;

    const defaultValue =
      values.find((v) => Number(v.is_default) === 1) || values[0];

    selections[group.id] = defaultValue;
  });

  return selections;
}

function getSelectedTotal(product, selectedOptions) {
  const activeGroups = (product.option_groups || []).filter(
    (group) => Number(group.is_active) === 1,
  );

  for (const group of activeGroups) {
    const selected = selectedOptions[group.id];

    if (selected) {
      const selectedPrice = Number(selected.price);
      if (!Number.isNaN(selectedPrice) && selectedPrice > 0) {
        return selectedPrice;
      }
    }

    const activeValues = (group.values || []).filter(
      (value) => Number(value.is_active) === 1,
    );

    const defaultValue =
      activeValues.find((value) => Number(value.is_default) === 1) ||
      activeValues.find((value) => {
        const price = Number(value.price);
        return !Number.isNaN(price) && price > 0;
      });

    if (defaultValue) {
      const defaultPrice = Number(defaultValue.price);
      if (!Number.isNaN(defaultPrice) && defaultPrice > 0) {
        return defaultPrice;
      }
    }
  }

  return Number(product.base_price || 0);
}

export default function ShopProductDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { apiFetch } = useApi();
  const { addItem } = useCart();

  const fileInputRef = useRef(null);

  const [product, setProduct] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [customText, setCustomText] = useState("");
  const [showPersonaliseModal, setShowPersonaliseModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [toast, setToast] = useState({ show: false, message: "" });
  const [uploadingFiles, setUploadingFiles] = useState(false);

  const showToast = (message) => {
    setToast({ show: true, message });
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => {
      setToast({ show: false, message: "" });
    }, 2200);
  };

  useEffect(() => {
    let mounted = true;

    const loadProduct = async () => {
      try {
        setLoading(true);
        setLoadError("");

        const res = await apiFetch("get_public_shop_product", {
          id: Number(id),
        });

        if (!res?.success) {
          throw new Error(res?.message || "Failed to load product.");
        }

        if (!mounted) return;

        setProduct(res.data);
        setSelectedOptions(getDefaultSelections(res.data));
        setExpandedGroups({});
        setUploadedFiles([]);
        setCustomText("");
        setShowPersonaliseModal(false);
      } catch (err) {
        if (!mounted) return;
        setLoadError(err.message || "Unable to load product.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadProduct();

    return () => {
      mounted = false;
    };
  }, [apiFetch, id]);

  const updateSelectedOption = (groupId, value) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [groupId]: value,
    }));
  };

  const toggleGroupExpanded = (groupId) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  const requiresPhotoUpload = Number(product?.requires_photo_upload) === 1;
  const allowsCustomText = Number(product?.allows_custom_text) === 1;
  const customTextLabel = product?.custom_text_label?.trim() || "Your text";
  const customTextMaxLength = Number(product?.custom_text_max_length || 0);
  const isPhotoPrintProduct =
    Number(product?.category_id) === PHOTO_PRINT_CATEGORY_ID;

  const handleUploadClick = () => {
    if (!product || !requiresPhotoUpload || uploadingFiles) return;
    fileInputRef.current?.click();
  };

  const uploadSingleFile = async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    const res = await apiFetch("upload_shop_temp_file", formData, {
      isFormData: true,
    });

    if (!res?.success || !res?.file_path) {
      throw new Error(res?.message || `Failed to upload ${file.name}`);
    }

    const persistentUrl = buildUploadedFileUrl(res.file_path);

    return {
      name: file.name,
      type: file.type || "",
      preview: persistentUrl,
      file_path: res.file_path,
      temp_path: res.file_path,
      uploaded: true,
    };
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    const maxUploads = Number(product?.max_uploads || 1);

    if (files.length === 0) return;

    const remainingSlots = Math.max(0, maxUploads - uploadedFiles.length);

    if (remainingSlots <= 0) {
      showToast(
        `You can upload a maximum of ${maxUploads} image${maxUploads > 1 ? "s" : ""}`,
      );
      e.target.value = "";
      return;
    }

    if (files.length > remainingSlots) {
      showToast(
        `You can only add ${remainingSlots} more image${remainingSlots > 1 ? "s" : ""}`,
      );
      e.target.value = "";
      return;
    }

    try {
      setUploadingFiles(true);

      const uploadedBatch = [];

      for (const file of files) {
        const uploaded = await uploadSingleFile(file);
        uploadedBatch.push(uploaded);
      }

      setUploadedFiles((prev) => [...prev, ...uploadedBatch]);

      showToast(
        `${uploadedBatch.length} image${uploadedBatch.length > 1 ? "s" : ""} uploaded`,
      );
    } catch (err) {
      showToast(err.message || "Failed to upload image");
    } finally {
      setUploadingFiles(false);
      e.target.value = "";
    }
  };

  const removeUploadedFile = (indexToRemove) => {
    setUploadedFiles((prev) =>
      prev.filter((_, index) => index !== indexToRemove),
    );
  };

  const unitPrice = useMemo(() => {
    return product ? getSelectedTotal(product, selectedOptions) : 0;
  }, [product, selectedOptions]);

  const derivedQty = useMemo(() => {
    if (isPhotoPrintProduct) {
      return uploadedFiles.length;
    }
    return 1;
  }, [isPhotoPrintProduct, uploadedFiles]);

  const displayTotal = useMemo(() => {
    if (isPhotoPrintProduct) {
      return Number(unitPrice || 0) * Number(derivedQty || 0);
    }
    return Number(unitPrice || 0);
  }, [isPhotoPrintProduct, unitPrice, derivedQty]);

  const validateSelections = () => {
    if (!product) return false;

    const requiredGroups = (product.option_groups || []).filter(
      (group) => Number(group.is_required) === 1,
    );

    for (const group of requiredGroups) {
      if (!selectedOptions[group.id]) {
        showToast(`Please select ${group.name}`);
        return false;
      }
    }

    return true;
  };

  const validatePersonalisation = () => {
    if (!product) return false;

    if (requiresPhotoUpload && uploadedFiles.length === 0) {
      showToast("Please upload your photos first");
      return false;
    }

    if (uploadingFiles) {
      showToast("Please wait for image upload to finish");
      return false;
    }

    if (allowsCustomText) {
      const trimmed = customText.trim();

      if (!trimmed) {
        showToast(`Please enter ${customTextLabel.toLowerCase()}`);
        return false;
      }

      if (customTextMaxLength > 0 && trimmed.length > customTextMaxLength) {
        showToast(`Maximum ${customTextMaxLength} characters allowed`);
        return false;
      }
    }

    return true;
  };

  const buildCartPayload = () => ({
    ...product,
    image: getPrimaryImage(product),
    final_price: Number(unitPrice || 0),
    selected_options: Object.values(selectedOptions).map((value) => ({
      id: value.id,
      label: value.label,
      price: Number(value.price || 0),
    })),
    uploaded_files: uploadedFiles.map((file) => ({
      name: file.name,
      type: file.type,
      preview: file.preview,
      file_path: file.file_path,
      temp_path: file.temp_path,
      uploaded: !!file.file_path,
    })),
    custom_text: allowsCustomText ? customText.trim() : "",
    qty: isPhotoPrintProduct ? uploadedFiles.length : 1,
    quantity_locked: isPhotoPrintProduct ? 1 : 0,
  });

  const resetPersonalisationState = () => {
    setUploadedFiles([]);
    setCustomText("");
    setShowPersonaliseModal(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const addToCartHandler = () => {
    if (!product) return;
    if (!validateSelections()) return;

    if (requiresPhotoUpload && uploadedFiles.length === 0) {
      showToast("Please upload your photos first");
      return;
    }

    if (uploadingFiles) {
      showToast("Please wait for image upload to finish");
      return;
    }

    addItem(buildCartPayload());
    resetPersonalisationState();
    showToast(`${product.name} added to cart`);
  };

  const openPersonaliseModal = () => {
    if (!product) return;
    if (!validateSelections()) return;
    setShowPersonaliseModal(true);
  };

  const addPersonalisedToCartHandler = () => {
    if (!product) return;
    if (!validateSelections()) return;
    if (!validatePersonalisation()) return;

    addItem(buildCartPayload());
    resetPersonalisationState();
    showToast(`${product.name} added to cart`);
  };

  const isPhotoUploadIncomplete =
    requiresPhotoUpload && !allowsCustomText && uploadedFiles.length === 0;

  return (
    <main className="mt-8 min-h-screen bg-[#f5f5f5] pt-28 text-black">
      <div
        className={[
          "fixed left-1/2 top-5 z-[60] -translate-x-1/2 transition-all duration-200",
          toast.show
            ? "translate-y-0 opacity-100"
            : "pointer-events-none -translate-y-2 opacity-0",
        ].join(" ")}
        aria-live="polite"
      >
        <div className="rounded-2xl bg-green-500 px-4 py-3 text-sm font-semibold text-white shadow-lg">
          {toast.message}
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-5 pb-16">
        <button
          onClick={() => navigate("/shop")}
          className="mb-6 text-sm font-semibold text-black/70 hover:text-black"
        >
          ← Back to shop
        </button>

        {loading ? (
          <p>Loading...</p>
        ) : loadError ? (
          <p className="text-red-600">{loadError}</p>
        ) : !product ? (
          <p>Product not found</p>
        ) : (
          <>
            <div className="grid gap-8 lg:grid-cols-[1.1fr_1fr]">
              <div className="bg-white">
                <div className="aspect-square w-full bg-black/5">
                  <img
                    src={getPrimaryImage(product)}
                    alt={product.name}
                    className="h-full w-full object-cover"
                  />
                </div>
              </div>

              <div className="bg-white p-6 md:p-8">
                <h1 className="text-3xl font-semibold text-[#2d2d2d]">
                  {product.name}
                </h1>

                {(product.option_groups || []).filter(
                  (group) => Number(group.is_active) === 1,
                ).length > 0 && (
                  <div className="mt-6 space-y-6">
                    <p className="text-lg font-medium">
                      Please Choose an Option
                    </p>

                    {(product.option_groups || [])
                      .filter((group) => Number(group.is_active) === 1)
                      .map((group) => {
                        const activeValues = (group.values || []).filter(
                          (value) => Number(value.is_active) === 1,
                        );

                        const isExpanded = !!expandedGroups[group.id];
                        const shouldCollapse =
                          activeValues.length > INITIAL_VISIBLE_OPTIONS;

                        const visibleValues =
                          shouldCollapse && !isExpanded
                            ? activeValues.slice(0, INITIAL_VISIBLE_OPTIONS)
                            : activeValues;

                        return (
                          <div key={group.id}>
                            <p className="mb-3 font-medium">{group.name}:</p>

                            <div className="flex flex-wrap gap-3">
                              {visibleValues.map((value) => {
                                const isSelected =
                                  selectedOptions[group.id]?.id === value.id;

                                return (
                                  <button
                                    key={value.id}
                                    type="button"
                                    onClick={() =>
                                      updateSelectedOption(group.id, value)
                                    }
                                    className={[
                                      "border px-4 py-2 text-sm transition duration-200",
                                      isSelected
                                        ? "border-pink-500 bg-pink-500 text-white"
                                        : "border-gray-300 bg-white text-[#3a3a3a] hover:border-pink-400 hover:bg-pink-50",
                                    ].join(" ")}
                                  >
                                    {value.label}
                                  </button>
                                );
                              })}
                            </div>

                            {shouldCollapse && (
                              <button
                                type="button"
                                onClick={() => toggleGroupExpanded(group.id)}
                                className="mt-4 text-sm font-semibold text-pink-500 hover:text-pink-600"
                              >
                                {isExpanded ? "− See less" : "+ See more"}
                              </button>
                            )}
                          </div>
                        );
                      })}
                  </div>
                )}

                <div className="mt-8">
                  {isPhotoPrintProduct ? (
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-semibold text-pink-500">
                          Price per print:
                        </p>
                        <p className="text-3xl font-bold">{money(unitPrice)}</p>
                      </div>

                      {uploadedFiles.length > 0 && (
                        <div className="flex items-center justify-between rounded-2xl bg-black/[0.03] px-4 py-3">
                          <div>
                            <p className="text-sm font-semibold text-black/60">
                              Quantity
                            </p>
                            <p className="text-lg font-semibold">
                              {derivedQty}
                            </p>
                          </div>

                          <div className="text-right">
                            <p className="text-sm font-semibold text-pink-500">
                              Total
                            </p>
                            <p className="text-2xl font-bold">
                              {money(displayTotal)}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm font-semibold text-pink-500">
                        Price:
                      </p>
                      <p className="text-3xl font-bold">{money(unitPrice)}</p>
                    </div>
                  )}
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple={Number(product.max_uploads || 1) > 1}
                  onChange={handleFileChange}
                  className="hidden"
                />

                <div className="mt-6 flex flex-wrap gap-4">
                  {requiresPhotoUpload && !allowsCustomText && (
                    <button
                      type="button"
                      onClick={handleUploadClick}
                      disabled={uploadingFiles}
                      className={[
                        "px-6 py-3 font-semibold text-white transition",
                        uploadingFiles
                          ? "cursor-not-allowed bg-pink-300"
                          : "bg-pink-500 hover:bg-pink-600",
                      ].join(" ")}
                    >
                      {uploadingFiles ? "Uploading..." : "Upload photos"}
                    </button>
                  )}

                  {allowsCustomText ? (
                    <button
                      type="button"
                      onClick={openPersonaliseModal}
                      className="bg-pink-500 px-6 py-3 font-semibold text-white transition hover:bg-pink-600"
                    >
                      Personalise
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={addToCartHandler}
                      disabled={isPhotoUploadIncomplete || uploadingFiles}
                      className={[
                        "px-6 py-3 font-semibold transition",
                        isPhotoUploadIncomplete || uploadingFiles
                          ? "cursor-not-allowed border border-black/10 bg-black/30 text-white/70"
                          : "border border-black/20 bg-black text-white hover:opacity-90",
                      ].join(" ")}
                    >
                      {uploadingFiles ? "Uploading..." : "Add to cart"}
                    </button>
                  )}
                </div>

                {requiresPhotoUpload && !allowsCustomText && uploadingFiles && (
                  <p className="mt-4 text-sm text-black/60">
                    Uploading image...
                  </p>
                )}

                {requiresPhotoUpload &&
                  !allowsCustomText &&
                  uploadedFiles.length > 0 && (
                    <div className="mt-5">
                      <p className="mb-3 text-sm font-medium text-[#2d2d2d]">
                        Uploaded image{uploadedFiles.length > 1 ? "s" : ""}
                      </p>

                      <div className="flex flex-wrap gap-3">
                        {uploadedFiles.map((file, index) => (
                          <div
                            key={`${file.name}_${index}`}
                            className="relative h-20 w-20 overflow-hidden rounded border border-gray-200 bg-black/5"
                          >
                            <img
                              src={file.preview}
                              alt={file.name}
                              className="h-full w-full object-cover"
                            />
                            <button
                              type="button"
                              onClick={() => removeUploadedFile(index)}
                              className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-xs text-white"
                              aria-label="Remove image"
                            >
                              ×
                            </button>
                            {(file.uploaded || file.file_path) && (
                              <span className="absolute bottom-1 right-1 h-2 w-2 rounded-full bg-green-500 ring-1 ring-white" />
                            )}
                          </div>
                        ))}
                      </div>

                      <p className="mt-2 text-xs text-black/55">
                        {uploadedFiles.length} of{" "}
                        {Number(product.max_uploads || 1)} uploaded
                      </p>
                    </div>
                  )}
              </div>
            </div>

            <div className="mt-10 bg-white p-6 md:p-8">
              <h2 className="text-2xl font-semibold">Description</h2>

              <div className="mt-4 space-y-4 text-sm leading-7 text-black/70">
                <p className="whitespace-pre-line">
                  {product.details || "No additional details available."}
                </p>
              </div>
            </div>
          </>
        )}
      </div>

      <PersonalisationModal
        show={showPersonaliseModal}
        product={product}
        getPrimaryImage={getPrimaryImage}
        requiresPhotoUpload={requiresPhotoUpload}
        allowsCustomText={allowsCustomText}
        customTextLabel={customTextLabel}
        customText={customText}
        customTextMaxLength={customTextMaxLength}
        setCustomText={setCustomText}
        uploadedFiles={uploadedFiles}
        uploadingFiles={uploadingFiles}
        handleUploadClick={handleUploadClick}
        removeUploadedFile={removeUploadedFile}
        isPhotoPrintProduct={isPhotoPrintProduct}
        displayTotal={displayTotal}
        unitPrice={unitPrice}
        addPersonalisedToCartHandler={addPersonalisedToCartHandler}
        setShowPersonaliseModal={setShowPersonaliseModal}
      />
    </main>
  );
}
